import {
    createProductSurcharge as createProductSurchargeHttpRequest,
    fetchProductSurcharges as fetchProductSurchargesHttpRequest,
    updateProductSurcharge as updateProductSurchargeHttpRequest,
    deleteProductSurcharge as deleteProductSurchargeHttpRequest,
} from '../api.js';

export const REQUEST_PRODUCT_SURCHARGES = 'REQUEST_PRODUCT_SURCHARGES';
export const RECEIVE_PRODUCT_SURCHARGES = 'RECEIVE_PRODUCT_SURCHARGES';
export const RECEIVE_PRODUCT_SURCHARGES_ERROR = 'RECEIVE_PRODUCT_SURCHARGES_ERROR';

export const REQUEST_CREATE_PRODUCT_SURCHARGE = 'REQUEST_CREATE_PRODUCT_SURCHARGE';
export const RECEIVE_CREATE_PRODUCT_SURCHARGE = 'RECEIVE_CREATE_PRODUCT_SURCHARGE';
export const RECEIVE_CREATE_PRODUCT_SURCHARGE_ERROR = 'RECEIVE_CREATE_PRODUCT_SURCHARGE_ERROR';

export const REQUEST_UPDATE_PRODUCT_SURCHARGE = 'REQUEST_UPDATE_PRODUCT_SURCHARGE';
export const RECEIVE_UPDATE_PRODUCT_SURCHARGE = 'RECEIVE_UPDATE_PRODUCT_SURCHARGE';
export const RECEIVE_UPDATE_PRODUCT_SURCHARGE_ERROR = 'RECEIVE_UPDATE_PRODUCT_SURCHARGE_ERROR';

export const REQUEST_DELETE_PRODUCT_SURCHARGE = 'REQUEST_DELETE_PRODUCT_SURCHARGE';
export const RECEIVE_DELETE_PRODUCT_SURCHARGE = 'RECEIVE_DELETE_PRODUCT_SURCHARGE';
export const RECEIVE_DELETE_PRODUCT_SURCHARGE_ERROR = 'RECEIVE_DELETE_PRODUCT_SURCHARGE_ERROR';

export const DISMISS_PRODUCT_SURCHARGE_RESULT = 'DISMISS_PRODUCT_SURCHARGE_RESULT';

export const RESET_PRODUCT_SURCHARGES = 'RESET_PRODUCT_SURCHARGES';

export const SEARCH_PRODUCT_SURCHARGE_QUERY = 'SEARCH_PRODUCT_SURCHARGE_QUERY';

/**
 * Request product surcharges Redux action creator
 */
function requestProductSurcharges() {
    return {
        type: REQUEST_PRODUCT_SURCHARGES,
    };
}

/**
 * Receive product surcharges Redux action creator
 * @param {array} productSurcharges Array of product surcharges received from root api
 */
function receiveProductSurcharges(productSurcharges) {
    return {
        type: RECEIVE_PRODUCT_SURCHARGES,
        productSurcharges,
    };
}

/**
 * Receive product surcharges error Redux action creator
 * @param {Error} error The error object created in api.js
 */
function receiveProductSurchargesError(error) {
    return {
        type: RECEIVE_PRODUCT_SURCHARGES_ERROR,
        error: error,
    };
}

/**
 * Thunk action for fetching product surcharges
 */
export function fetchProductSurcharges() {
    return (dispatch, getState) => {
        dispatch(requestProductSurcharges());

        const user = getState().user;
        return fetchProductSurchargesHttpRequest(user.group)
            .then(response => dispatch(receiveProductSurcharges(response)))
            .catch(error => dispatch(receiveProductSurchargesError(error)));
    };
}

/**
 * Request create product surcharge Redux action creator
 */
function requestCreateProductSurcharge() {
    return {
        type: REQUEST_CREATE_PRODUCT_SURCHARGE,
    };
}

/**
 * Receive create product surcharge Redux action creator
 * @param {object} productSurcharge The new product surcharge object that is created. Coming from root API.
 */
function receiveCreateProductSurcharge(productSurcharge) {
    return {
        type: RECEIVE_UPDATE_PRODUCT_SURCHARGE,
        productSurcharge,
    };
}

/**
 * Receive product surcharge error Redux action creator
 * @param {Error} error The Error object received from api.js while creating a new product surcharge
 */
function receiveCreateProductSurchargeError(error) {
    return {
        type: RECEIVE_CREATE_PRODUCT_SURCHARGE_ERROR,
        error,
    };
}

/**
 * Thunk action for creating a new product surcharge
 * @param {object} productSurcharge The new product surcharge object to be created in db.
 */
export function createProductSurcharge(productSurcharge) {
    return (dispatch, getState) => {
        dispatch(requestCreateProductSurcharge());

        const user = getState().user;
        return createProductSurchargeHttpRequest(productSurcharge, user.group)
            .then(createdProductSurcharge => dispatch(receiveCreateProductSurcharge(createdProductSurcharge)))
            .catch(error => dispatch(receiveCreateProductSurchargeError(error)));
    };
}

/**
 * Request update product surcharge Redux action creator
 */
function requestUpdateProductSurcharge() {
    return {
        type: REQUEST_UPDATE_PRODUCT_SURCHARGE,
    };
}

/**
 * Receive update product surcharge Redux action creator
 * @param {object} productSurcharge The updated product surcharge object received from root api
 */
function receiveUpdateProductSurcharge(productSurcharge) {
    return {
        type: RECEIVE_UPDATE_PRODUCT_SURCHARGE,
        productSurcharge,
    };
}

/**
 * Receive update product surcharge error Redux action creator
 * @param {Error} error The error object received from api.js while handling update product surcharge
 */
function receiveUpdateProductSurchargeError(error) {
    return {
        type: RECEIVE_UPDATE_PRODUCT_SURCHARGE_ERROR,
        error,
    };
}

/**
 * Thunk action for updating a product surcharge
 * @param {string} id The id of the product surcharge which needs to be updated
 * @param {object} productSurchargeToBeUpdated The product surcharge object to be updated
 */
export function updateProductSurcharge(id, productSurchargeToBeUpdated) {
    return (dispatch, getState) => {
        dispatch(requestUpdateProductSurcharge());

        const user = getState().user;
        return updateProductSurchargeHttpRequest(id, productSurchargeToBeUpdated, user.group)
            .then(updatedProductSurcharge => dispatch(receiveUpdateProductSurcharge(updatedProductSurcharge)))
            .catch(error => dispatch(receiveUpdateProductSurchargeError(error)));
    };
}

/**
 * Request inactive product surcharge Redux action creator
 */
function requestDeleteProductSurcharge() {
    return {
        type: REQUEST_DELETE_PRODUCT_SURCHARGE,
    };
}

/**
 * Receive inactive product surcharge Redux action creator
 * @param {string} id Th id of the deleted product surcharge
 */
function receiveDeleteProductSurcharge(id) {
    return {
        type: RECEIVE_DELETE_PRODUCT_SURCHARGE,
        id,
    };
}

/**
 * Receive inactive product surcharge error Redux action creator
 * @param {Error} error The error received from api.js.
 */
function receiveDeleteProductSurchargeError(error) {
    return {
        type: RECEIVE_DELETE_PRODUCT_SURCHARGE_ERROR,
        error,
    };
}

/**
 * Thunk action for inactivating a product surcharge
 * @param {number} id The id of the product surcharge to be inactivated
 */
export function deleteProductSurcharge(id) {
    return (dispatch, getState) => {
        dispatch(requestDeleteProductSurcharge());

        const user = getState().user;
        return deleteProductSurchargeHttpRequest(id, user.group)
            .then(id => dispatch(receiveDeleteProductSurcharge(id)))
            .catch(error => dispatch(receiveDeleteProductSurchargeError(error)));
    };
}

/**
 * Dismiss results of product surcharges requests Redux action creator
 */
export function dismissProductSurchargeResults() {
    return {
        type: DISMISS_PRODUCT_SURCHARGE_RESULT,
    };
}

/**
 * Resets product surcharges state to initial state
 */
function resetProductSurcharges() {
    return {
        type: RESET_PRODUCT_SURCHARGES,
    };
}

/**
 * Thunk action creator for refreshing product surcharges state
 */
export function refreshProductSurcharges() {
    return dispatch => {
        dispatch(resetProductSurcharges());
        dispatch(fetchProductSurcharges());
    };
}

/**
 * Thunk action for handling product surcharge search
 */
export function productSurchargeSearchQuery(query) {
    return dispatch => {
        dispatch({
            type: SEARCH_PRODUCT_SURCHARGE_QUERY,
            query,
        });
    };
}
